import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Fluent 2 Input Component
 * https://fluent2.microsoft.design/components/web/react/core/field/usage
 *
 * Features:
 * - Height: 36px (h-9) for better touch targets
 * - Motion: Fluent 2 easing for transitions
 * - Colors: Neutral palette with dark mode support
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
  success?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, success, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          // Base styles with Fluent 2 motion
          'flex h-9 w-full rounded-lg border bg-white px-3 py-2 text-sm',
          'transition-all duration-150 ease-[cubic-bezier(0.33,0,0.67,1)]',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          'placeholder:text-[#707070] dark:placeholder:text-[#999]',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-[#a3a3a3]',
          'dark:bg-[#292929] dark:disabled:bg-[#1a1a1a] dark:disabled:text-[#616161]',
          'text-[#1a1a1a] dark:text-white',
          // State variants
          error
            ? 'border-[#c50f1f] focus:ring-[#c50f1f]/25 dark:border-[#dc626d] dark:focus:ring-[#dc626d]/25'
            : success
              ? 'border-[#0e7a0d] focus:ring-[#0e7a0d]/25 dark:border-[#54b054] dark:focus:ring-[#54b054]/25'
              : 'border-[#e5e5e5] hover:border-[#d4d4d4] focus:ring-[#1a1a1a]/10 focus:border-[#1a1a1a] dark:border-[#3d3d3d] dark:hover:border-[#525252] dark:focus:ring-white/10 dark:focus:border-white',
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }
